import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Youtube, Facebook, Instagram, Music2, Send, Check, Loader2, Clock, AlertTriangle } from "lucide-react";
import { getSocialPlatforms, getSocialAccounts, publishVideo, errText } from "../lib/api";
import { devError } from "../lib/log";

const ICONS = { youtube: Youtube, facebook: Facebook, instagram: Instagram, tiktok: Music2 };

export default function PublishDialog({ videoId, open, onOpenChange, onPublished }) {
  const [platforms, setPlatforms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [schedule, setSchedule] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([getSocialPlatforms(), getSocialAccounts()])
      .then(([p, a]) => { setPlatforms(p); setAccounts(a); })
      .catch((e) => devError("load publish dialog:", e));
  }, [open]);

  const acc = (p) => accounts.find((a) => a.platform === p && a.status === "connected");
  const toggle = (p) => setSelected((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));

  const submit = async () => {
    if (!selected.length) { toast.error("Pick at least one connected platform"); return; }
    setBusy(true);
    try {
      const payload = { platforms: selected };
      if (schedule) payload.scheduled_at = new Date(schedule).toISOString();
      const res = await publishVideo(videoId, payload);
      const spaced = (res?.slots || []).filter((s) => s.spaced);
      if (spaced.length) {
        const when = spaced.map((s) => `${s.platform}: ${new Date(s.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`).join(", ");
        toast.success(`Queued — spaced ${res.min_gap_minutes} min apart per account (${when})`, { duration: 7000 });
      } else {
        toast.success(schedule ? "Scheduled for publishing" : "Publishing started");
      }
      onPublished && onPublished();
      onOpenChange(false);
      setSelected([]); setSchedule("");
    } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="publish-dialog" className="bg-[#11141E] border-white/10 text-white max-w-md">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Send className="h-5 w-5 text-[#FF6A5E]" /> Publish to social</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400">Select connected platforms. Publishing uses no extra video credits.</p>
        <div className="space-y-2 mt-2">
          {platforms.map((p) => {
            const Icon = ICONS[p.id] || Send;
            const connected = acc(p.id);
            const active = selected.includes(p.id);
            return (
              <button key={p.id} data-testid={`publish-platform-${p.id}`} disabled={!connected}
                onClick={() => connected && toggle(p.id)}
                className={`w-full flex items-center justify-between rounded-xl border px-3 py-2.5 text-left transition-colors ${active ? "border-[#FF453A] bg-[#FF453A]/10" : "border-white/10"} ${!connected ? "opacity-60" : "hover:bg-white/5"}`}>
                <span className="flex items-center gap-2.5">
                  <Icon className="h-5 w-5" style={{ color: p.color }} />
                  <span>
                    <span className="text-sm font-medium block">{p.name}</span>
                    {connected ? <span className="text-xs text-slate-400">{connected.account_name}</span>
                      : !p.configured ? <span className="text-xs text-amber-400 flex items-center gap-1"><Clock className="h-3 w-3" /> Setup pending</span>
                      : <span className="text-xs text-slate-500">Not connected</span>}
                  </span>
                </span>
                {active && <Check className="h-4 w-4 text-[#FF6A5E]" />}
              </button>
            );
          })}
        </div>

        {accounts.filter((a) => a.status === "connected").length === 0 && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>No accounts connected yet. <Link to="/connections" className="underline font-semibold" onClick={() => onOpenChange(false)}>Connect an account</Link> to publish.</span>
          </div>
        )}

        {selected.includes("tiktok") && (
          <p data-testid="tiktok-privacy-note" className="text-xs text-slate-500 leading-relaxed">
            TikTok posts upload directly from our servers and are set to <span className="text-slate-300">Only me</span> until TikTok approves the app — switch visibility inside TikTok after it lands.
          </p>
        )}

        <div>
          <label className="text-xs text-slate-400">Schedule (optional)</label>
          <Input data-testid="publish-schedule" type="datetime-local" value={schedule} onChange={(e) => setSchedule(e.target.value)} className="mt-1 bg-[#0B0C10] border-white/10" />
        </div>

        <Button data-testid="publish-submit-btn" onClick={submit} disabled={busy || !selected.length}
          className="w-full rounded-full bg-[#FF453A] hover:bg-[#FF5D54] text-white font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : schedule ? "Schedule publishing" : `Publish to ${selected.length || 0} platform${selected.length === 1 ? "" : "s"}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
